/**
 * One campaign in the desktop table. The name is the row's only link, stretched
 * over the whole row, so clicking anywhere opens the campaign. Ended campaigns
 * are dimmed. For staff who may not edit campaigns the row is plain text.
 */
import { campaignDiscountLabel, TicketSalesCampaignStatus } from '@bonsai/shared';
import { Link } from 'react-router-dom';

import { campaignShortId, type CampaignListRow } from '../ticket-sales-campaigns-list-model';
import { campaignEditPath } from '../ticket-sales-campaigns-paths';

import { TicketSalesCampaignStatusCell } from './TicketSalesCampaignStatusCell';
import { TicketSalesCampaignWhenCell } from './TicketSalesCampaignWhenCell';

import './ticket-sales-campaigns-list.css';

export interface TicketSalesCampaignsTableRowProps {
  row: CampaignListRow;
  now: Date;
  canOpen: boolean;
}

const NAME_STYLE = { fontWeight: 700, fontSize: 13.5, color: 'var(--nb-ink)' } as const;

const DISCOUNT_CHIP_STYLE = {
  display: 'inline-flex',
  alignItems: 'center',
  height: 24,
  padding: '0 9px',
  borderRadius: 6,
  background: 'var(--nb-surface)',
  border: '1px solid var(--nb-border)',
  fontSize: 12.5,
  fontWeight: 800,
  color: 'var(--nb-ink)',
  whiteSpace: 'nowrap',
} as const;

export function TicketSalesCampaignsTableRow({
  row,
  now,
  canOpen,
}: TicketSalesCampaignsTableRowProps) {
  const { campaign, status } = row;
  return (
    <tr
      style={{
        position: 'relative',
        cursor: canOpen ? 'pointer' : undefined,
        opacity: status === TicketSalesCampaignStatus.ENDED ? 0.78 : 1,
      }}
    >
      <td style={{ paddingLeft: 18 }}>
        {canOpen ? (
          <Link
            to={campaignEditPath(campaign.id)}
            className="tsc-row-link"
            style={{ ...NAME_STYLE, textDecoration: 'none' }}
          >
            {campaign.name}
          </Link>
        ) : (
          <span style={NAME_STYLE}>{campaign.name}</span>
        )}
        <div className="nb-mono" style={{ fontSize: 10.5, color: 'var(--nb-faint)', marginTop: 1 }}>
          {campaignShortId(campaign.id)}
        </div>
      </td>
      <td>
        <span className="nb-tabular" style={DISCOUNT_CHIP_STYLE}>
          {campaignDiscountLabel(campaign)}
        </span>
      </td>
      <TicketSalesCampaignWhenCell instant={campaign.startsAt} />
      <TicketSalesCampaignWhenCell instant={campaign.endsAt} />
      <TicketSalesCampaignStatusCell row={row} now={now} showChevron={canOpen} />
    </tr>
  );
}
